import Vue from "vue";
import { BackEndService } from "../../../utils/BackEndService";
import { analyticsController } from "../analytics/AnalyticsController";
import { userService } from "../UserService";
import { type CurrentPromotion } from '../../global/marketing/PromotionController'
import { Notify, Platform } from "quasar";
import { type InAppPurchaseReturn } from "../../../boot/ios-purchase";

type StripePriceInterval = 'year' | 'month'
export type PlanType = 'monthly' | 'annual' | 'lifetime'

export const PlanTypeName: Record<PlanType, string> = {
  annual: 'Annuel',
  lifetime: 'À vie',
  monthly: 'Mensuel'
}
export const PlanTypeUnit: Record<PlanType, string> = {
  annual: 'par an',
  lifetime: '1 seule fois',
  monthly: 'par mois'
}

export interface StripePrice {
  id: string,
  unit_amount: number,
  type: 'recurring' | 'one_time'
  recurring?: {
    interval: StripePriceInterval
    interval_count: number
    trial_period_days: number
  }
  planType: PlanType
}
export interface StripeProduct {
  id: string,
  prices: StripePrice[]
}
interface SubscriptionServiceState {
  product: StripeProduct | null
}

class SubscriptionService {
  public readonly state = Vue.observable<SubscriptionServiceState>({
    product: null,
  })

  constructor() {
    userService.on('userService_loaded', this.fetchSubscriptions.bind(this))
  }

  get monthlyPrice(): StripePrice | null {
    if (!this.state.product) {
      return null
    }
    return this.state.product.prices.find((price) => price.planType === 'monthly') || null
  }

  public humanizeUnitAmount(price: StripePrice, promotion?: CurrentPromotion) {
    if (!price) {
      return ''
    }

    if (promotion) {
      return ((price.unit_amount - price.unit_amount * (promotion.value / 100)) / 100).toFixed(2) + '€'
    }

    return (price.unit_amount / 100).toFixed(2) + '€'
  }
  public amountToHumanizeMonthlyCost(price: StripePrice, promotion?: CurrentPromotion) {
    if (!price.recurring) {
      return this.humanizeUnitAmount(price, promotion)
    }

    const intervalValue: Record<StripePriceInterval, number> = {
      'year': 12,
      'month': 1
    };

    if (promotion) {
      return ((price.unit_amount - price.unit_amount * (promotion.value / 100)) / 100 / intervalValue[price.recurring.interval]).toFixed(2) + '€'
    }

    return ((price.unit_amount / 100) / intervalValue[price.recurring.interval]).toFixed(2) + '€'

  }

  public fetchSubscriptions(): Promise<void> {
    return BackEndService.sendGetRequest('subscriptions').then((response) => {
      this.state.product = response.content;
    });
  }

  public generateBillingPortalLink() {
    return BackEndService.sendGetRequest(`subscriptions/billing-redirection`)
  }
  public generateCheckoutPortalLink(priceId: string, promoCode?: string, from = 'subscribe', fromFreemiumPopup = false) {
    let queryParams = `?from=${from}`

    if (promoCode) {
      queryParams += `&promoCode=${promoCode}`
    }

    if (fromFreemiumPopup) {
      queryParams += `&freemiumEnd=true`
    }

    return BackEndService.sendGetRequest(`subscriptions/checkout-redirection/${priceId}${queryParams}`);
  }

  public enableTrial() {
    analyticsController.logBasicAnalyticsEvent('user started trial period', 'Subscription');

    return BackEndService.sendPostRequest('trial/enable').then((response) => {
      userService.setUserData(response.data);
    })
  }

  public redirectToCheckout(price: StripePrice, currentPromotion?: CurrentPromotion, from = 'subscribe', fromFreemiumPopup = false): Promise<string> {
    const isPromoAvailable = currentPromotion?.isInProgress && (currentPromotion?.wichItems.includes(price.planType) || currentPromotion?.wichItems.includes('all'))
    if (Platform.is.ios && Platform.is.capacitor) {
      const rcPlan: string = Vue.prototype.InAppPurchases.PlanTypeToRcPlan[`${price.planType}${(isPromoAvailable && currentPromotion?.isSuperPromotion) ? '_super_promo' : (isPromoAvailable || fromFreemiumPopup) ? '_promo' : ''}`]

      return Vue.prototype.InAppPurchases.getOfferings().then((res) => {
        const rcPackage = res.current.availablePackages.find((el) => el.identifier === rcPlan)

        if(!rcPackage) {
          Notify.create({
            message: "Le package n'existe pas. Veuillez réessayer ou contacter le support.",
            color: 'negative',
            position: 'top',
            icon: 'mdi-alert-circle-outline',
            timeout: 5000,
          });

          return 'ios_purchase_failed'
        }

        return Vue.prototype.InAppPurchases.purchase(rcPackage).then((res: InAppPurchaseReturn) => {
          console.log('purchase here', res);
          if (res.type === 'purchase_failed') {
            analyticsController.logAnalyticsEvent(
              {
                title: 'purchase failed',
                category: 'Subscription',
                channel: 'paymentflow',
                additionalParameters: {
                  paymentGroup: userService.user.paymentGroupTesting,
                  priceId: price.id,
                  planType: price.planType,
                  platform: 'ios',
                  reason: res.reason,
                },
              },
              'allWithSlack'
            );
            Notify.create({
              message: "Une erreur est survenue lors de l'achat. Veuillez réessayer ou contacter le support.",
              color: 'negative',
              position: 'top',
              icon: 'mdi-alert-circle-outline',
              timeout: 5000,
            });

            return 'ios_purchase_failed'
          } else if (res.type === 'purchase_success') {
            analyticsController.logAnalyticsEvent(
              {
                title: 'purchase success',
                category: 'Subscription',
                channel: 'subs',
                additionalParameters: {
                  paymentGroup: userService.user.paymentGroupTesting,
                  priceId: price.planType,
                  planType: price.planType,
                  platform: 'ios',
                },
              },
              'allWithSlack'
            );
            Notify.create({
              message: 'Votre achat a bien été effectué !',
              color: 'positive',
              position: 'top',
              classes: 'teacher__notification',
              icon: 'mdi-check-circle-outline',
              timeout: 5000,
            });
            return 'ios_purchase_success'
          }
        });
      });
    } else {
      const promoCode = isPromoAvailable ? currentPromotion.code : undefined;
      return this.generateCheckoutPortalLink(price.id, promoCode, from, fromFreemiumPopup).then((res) => (window.location.href = res.content))
    }
  }

}

export const subscriptionService = new SubscriptionService();
